import React from 'react';
import {
  Snackbar,
  SnackbarCloseReason,
  SnackbarContent,
  SnackbarContentClasses,
  SnackbarOrigin,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

export type ActionFeedbackType = 'success' | 'danger' | 'warning' | 'info';

export interface ActionFeedbackProps {
  message: React.ReactNode;
  open?: boolean;
  type?: ActionFeedbackType;
  anchorOrigin?: SnackbarOrigin;
  onClose?: (event?: React.SyntheticEvent<any> | Event, reason?: SnackbarCloseReason) => void;
  action?: React.ReactNode;
  classes?: Partial<SnackbarContentClasses>;
  transitionDuration?: number;
  autoHideDuration?: number;
  style?: React.CSSProperties;
}

const ActionsFeedback: React.FC<ActionFeedbackProps> = ({
  open,
  type = 'info',
  message,
  autoHideDuration = 5000,
  anchorOrigin = {
    vertical: 'bottom',
    horizontal: 'left',
  },
  action,
  classes,
  transitionDuration = 0,
  onClose,
  style = {},
}) => {
  const theme = useTheme();

  // Mapping 'danger' to 'error' (as 'danger' is not a predefined color in MUI)
  const colorMapping: { [key in ActionFeedbackType]: keyof typeof theme.palette } = {
    success: 'success',
    danger: 'error',
    warning: 'warning',
    info: 'info',
  };

  const colorType = colorMapping[type];

  const color = theme.palette[colorType] as { light: string; main: string };

  return (
    <Snackbar
      ClickAwayListenerProps={{ onClickAway: () => null }}
      action={action}
      anchorOrigin={anchorOrigin}
      autoHideDuration={autoHideDuration}
      classes={classes}
      onClose={(e, r) => onClose && onClose(e, r)}
      open={open}
      transitionDuration={transitionDuration}
      style={style}
    >
      <div>
        <SnackbarContent
          className={`${type}`}
          style={{
            backgroundColor: color.light,
            color: color.main,
          }}
          message={
            <Typography variant="body-small-500" component="p">
              {message}
            </Typography>
          }
        />
      </div>
    </Snackbar>
  );
};

export default ActionsFeedback;
